import { useRef } from "react";
import { Button } from "../../../components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { DebugOverlay } from "../../../debug";
import { supabase } from "../../../lib/supabase";
import { X, Lock } from "lucide-react";
import { compareNames } from "../utils/attendanceHelpers";
import { EditStudentRow } from "./EditStudentRow";
import { BandLogEntryForm } from "../../baende/components/BandLogEntryForm";
import type { BandLogEntryFormHandle } from "../../baende/components/BandLogEntryForm";
import { ConcurrentCoursePanel } from "./ConcurrentCoursePanel";
import { DoppelstundeInfoPanel } from "./DoppelstundeInfoPanel";
import { MarkAllHeader } from "./MarkAllHeader";
import { TagesgastCard } from "./TagesgastCard";
import {
  EarlierPresentSaveDialog,
  type EarlierPresentDecision,
} from "./EarlierPresentSaveDialog";
import {
  useRemoveCourseGuestEnrollmentMutation,
  useSetCourseShortNoticeCancelledMutation,
} from "../api/lessonAttendanceEndpoints";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ConcurrentCoursePanelProps } from "./ConcurrentCoursePanel";
import type { DoppelstundeInfoPanelProps } from "./DoppelstundeInfoPanel";
import type { TempAttendanceMap } from "../types/attendanceDialog";

// ============================================================================
// Types
// ============================================================================

export interface EditSectionProps extends DoppelstundeInfoPanelProps {
  selectedLesson: any;
  isMobile: boolean;
  recordedMeta: { name: string; atISO: string } | null;
  prefilledFromPrevious: boolean;
  prefilledAny: boolean;
  /** When true, bypass the prefill summary card and render the edit grid
   *  immediately. Set by callers that already showed a Bestätigen/Anpassen
   *  prompt outside the dialog (e.g. the Unterrichtsliste's inline
   *  attendance widget) so the user doesn't see the same card twice. */
  skipPrefillSummary?: boolean;
  diaryMeta: { name: string; atISO: string } | null;
  lessonNote: string;
  setLessonNote: (v: string) => void;
  /** Hausaufgaben for this lesson. Inline textarea below the Klassenbuch
   *  entry. Persisted by useAttendanceSave only when changed. */
  lessonHomework?: string;
  setLessonHomework?: (v: string) => void;
  /** Pristine homework value at load time — used by the save hook to
   *  decide whether to fire the diary-upsert RPC. */
  initialLessonHomework?: string;
  tempAttendance: TempAttendanceMap;
  setTempAttendance: React.Dispatch<React.SetStateAction<TempAttendanceMap>>;
  kbStudentIds: Set<string>;
  kbStudentTimes: Map<string, { start: string; end: string }>;
  earlierPresentIds: Set<string>;
  frozenEditOrderRef: React.MutableRefObject<string[]>;
  frozenEditLessonIdRef: React.MutableRefObject<string | null>;
  saveAttendance: (
    entryOverrides?: Record<
      string,
      {
        leftSchool?: boolean;
        elsewhereLocation?: string;
        excuseReason?: string;
        status?: "present" | "late" | "excused" | "unexcused";
      }
    >,
  ) => Promise<void>;
  /**
   * Per-student protection metadata from the attendance bundle. The
   * EarlierPresentSaveDialog needs the `reasons` array to decide whether
   * an absence note already explains why the kid is leaving — when one
   * does, the Auschecken path skips the required Begründung field.
   */
  protectionByStudent?: Record<
    string,
    { protected: boolean; reasons: string[]; hasAnyToday: boolean; isAbsentToday: boolean }
  >;
  setStudentStatus: (
    studentId: string,
    status: "present" | "late" | "excused" | "unexcused",
  ) => void;
  setAllStudentsStatus: (
    status: "present" | "late" | "excused" | "unexcused",
  ) => void;
  formatStudentName: (name: string) => string;
  // ConcurrentCoursePanel pass-through
  courseOverlaps?: ConcurrentCoursePanelProps["courseOverlaps"];
  concurrentCourseAttendance: any;
  transferredStudentIds: Set<string>;
  lessonId?: string;
  isTransferring: boolean;
  pendingTransfer: ConcurrentCoursePanelProps["pendingTransfer"];
  setPendingTransfer: ConcurrentCoursePanelProps["setPendingTransfer"];
  transferReason: string;
  setTransferReason: (v: string) => void;
  onTransferConfirm: (studentId: string, fromLessonId: string) => Promise<void>;
  /** Students kept in main lesson (for course popup banner) */
  keptInMainLesson?: Array<{
    id: string;
    name: string;
    notes: string;
    fromLessonId: string | null;
    mode: "present" | "pending";
  }>;
  reloadBundle: () => Promise<void>;
  onReturnToCourseConfirm: (
    studentId: string,
    fromMainLessonId: string,
  ) => Promise<void>;
  handleStayedWithClassTransfer: (
    studentId: string,
    reason: string,
  ) => Promise<boolean>;
  /**
   * Teacher user_id override for kiosk callers — passed to nested RPCs
   * that would otherwise rely on `auth.uid()` server-side (which on a
   * paired-kiosk JWT resolves to kiosk_id, not in user_profiles).
   * Currently used for `ops_check_out_student_react` + Tagesgast
   * card; undefined on the authenticated browser-tab path.
   */
  userIdOverride?: string | null;
}

// ============================================================================
// Component
// ============================================================================

export function EditSection({
  selectedLesson,
  isMobile,
  recordedMeta,
  prefilledFromPrevious,
  prefilledAny,
  skipPrefillSummary,
  diaryMeta,
  lessonNote,
  setLessonNote,
  lessonHomework = "",
  setLessonHomework,
  initialLessonHomework = "",
  tempAttendance,
  setTempAttendance,
  kbStudentIds,
  kbStudentTimes,
  earlierPresentIds,
  frozenEditOrderRef,
  frozenEditLessonIdRef,
  saveAttendance,
  setStudentStatus,
  setAllStudentsStatus,
  formatStudentName,
  protectionByStudent,
  courseOverlaps,
  concurrentCourseAttendance,
  transferredStudentIds,
  lessonId,
  isTransferring,
  pendingTransfer,
  setPendingTransfer,
  transferReason,
  setTransferReason,
  onTransferConfirm,
  nextLessonCourses,
  nextLessonIsCancelled,
  nextLessonSubject,
  keptInMainLesson = [],
  reloadBundle,
  onReturnToCourseConfirm,
  handleStayedWithClassTransfer,
  userIdOverride,
}: EditSectionProps) {
  const isCourse = !!(selectedLesson as any)?.is_course;
  const bandFormRef = useRef<BandLogEntryFormHandle>(null);

  // Earlier-present save guard: if the teacher is marking any student absent
  // who was already present (or late) earlier today, ask once whether each
  // such student left the school or is in another offer before saving.
  const [earlierGuardOpen, setEarlierGuardOpen] = useState(false);
  const [earlierGuardStudents, setEarlierGuardStudents] = useState<
    Array<{ id: string; name: string }>
  >([]);
  /**
   * Per-student absence-note text (reason / decision_comment) for the
   * lesson's date. Populated lazily when the earlier-present dialog opens
   * so we can render the actual note rather than a generic
   * "Abwesenheitsnotiz" badge.
   */
  const [earlierGuardNoteText, setEarlierGuardNoteText] = useState<
    Map<string, string>
  >(new Map());

  // Fetch absence-note text for the candidate students when the dialog
  // opens. Strictly best-effort: if the query fails or returns nothing,
  // the dialog just falls back to no badge text (Auschecken still works).
  useEffect(() => {
    if (!earlierGuardOpen || earlierGuardStudents.length === 0) return;
    const schoolId = (selectedLesson as any)?.school_id;
    const startDt = (selectedLesson as any)?.start_datetime;
    if (!schoolId || !startDt) return;
    const dateIso = String(startDt).slice(0, 10);
    const ids = earlierGuardStudents.map((s) => s.id);
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("student_absence_notes" as any)
          .select(
            "student_id, reason, decision_comment, absence_status, absence_type, start_date, end_date" as any,
          )
          .eq("school_id", schoolId as any)
          .in("student_id", ids as any)
          .lte("start_date", dateIso as any)
          .gte("end_date", dateIso as any);
        if (cancelled) return;
        if (error || !Array.isArray(data)) {
          setEarlierGuardNoteText(new Map());
          return;
        }
        const map = new Map<string, string>();
        for (const row of data as any[]) {
          const sid = String(row.student_id || "");
          if (!sid) continue;
          // Prefer reason (the teacher/parent's free-text), fall back to
          // decision_comment, then a friendly absence_status label.
          const t =
            (row.reason && String(row.reason).trim()) ||
            (row.decision_comment && String(row.decision_comment).trim()) ||
            (row.absence_status && String(row.absence_status).trim()) ||
            "";
          if (t && !map.has(sid)) map.set(sid, t);
        }
        setEarlierGuardNoteText(map);
      } catch {
        if (!cancelled) setEarlierGuardNoteText(new Map());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [earlierGuardOpen, earlierGuardStudents, selectedLesson]);

  /**
   * Students who already have an EXCUSING absence note covering today
   * (Informiert / Krankgemeldet / Beurlaubt). For these kids the
   * EarlierPresentSaveDialog skips the required Begründung field on the
   * Auschecken path — the note already explains why they're leaving.
   *
   * Sourced from `protectionByStudent.reasons` (populated by the
   * attendance bundle's prefills). `daily_log.absent_unexcused` and
   * `daily_log.absent_unclear` are intentionally NOT included — those are
   * unresolved/Fehlzeit states, not "the parent told us they're sick".
   */
  const studentIdsWithCoveringAbsenceNote = useMemo(() => {
    const set = new Set<string>();
    if (!protectionByStudent) return set;
    for (const sid of Object.keys(protectionByStudent)) {
      const reasons = protectionByStudent[sid]?.reasons ?? [];
      if (
        reasons.includes("krankgemeldet") ||
        reasons.includes("daily_log.absent_excused")
      ) {
        set.add(sid);
      }
    }
    return set;
  }, [protectionByStudent]);

  /**
   * Students whose absence note actually covers THIS lesson's time
   * window — used to disable the per-row "U" (Ungeklärt) button.
   *
   * `protectionByStudent` above is date-level only, so a morning-only
   * sick note would still block "U" for an afternoon lesson. Here we
   * fetch each note's start_time / end_time and keep only the ones
   * that overlap the selected lesson's clock window. A note with no
   * times is full-day and covers everything; `verspätet` notes are
   * lateness, not an absence, so they don't gate "U".
   */
  const [lessonCoveredStudentIds, setLessonCoveredStudentIds] = useState<
    Set<string>
  >(new Set());
  useEffect(() => {
    const sl = selectedLesson as any;
    const schoolId = sl?.school_id;
    const startDt = sl?.start_datetime;
    const students = (sl?.students || []) as Array<{ id: string }>;
    if (!schoolId || !startDt || students.length === 0) {
      setLessonCoveredStudentIds(new Set());
      return;
    }
    const dateIso = String(startDt).slice(0, 10);
    const lessonStart = String(startDt).slice(11, 19) || "00:00:00";
    const lessonEnd = sl?.end_datetime
      ? String(sl.end_datetime).slice(11, 19) || "23:59:59"
      : "23:59:59";
    const ids = students.map((s) => s.id);
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("student_absence_notes" as any)
          .select("student_id, start_time, end_time, absence_status" as any)
          .eq("school_id", schoolId as any)
          .in("student_id", ids as any)
          .lte("start_date", dateIso as any)
          .gte("end_date", dateIso as any);
        if (cancelled) return;
        const set = new Set<string>();
        if (!error && Array.isArray(data)) {
          for (const row of data as any[]) {
            if (String(row.absence_status) === "verspätet") continue;
            // null times = full-day note → covers the whole day.
            const noteStart = row.start_time || "00:00:00";
            const noteEnd = row.end_time || "23:59:59";
            // Overlap test against the lesson's clock window.
            if (noteStart < lessonEnd && noteEnd > lessonStart) {
              set.add(String(row.student_id));
            }
          }
        }
        setLessonCoveredStudentIds(set);
      } catch {
        if (!cancelled) setLessonCoveredStudentIds(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLesson]);

  const performSave = useCallback(
    async (
      entryOverrides?: Record<
        string,
        { leftSchool?: boolean; elsewhereLocation?: string }
      >,
    ) => {
      const bandResult = await bandFormRef.current?.triggerSave();
      if (bandResult && !bandResult.ok && bandResult.reason !== "incomplete") {
        return;
      }
      await saveAttendance(entryOverrides);
    },
    [saveAttendance],
  );

  const handleSaveAll = async () => {
    // Find students newly marked Entschuldigt (excused only — unentschuldigt
    // doesn't need the "Auschecken vs Sonstiges" distinction; if the teacher
    // chose U they're saying "no plausible reason given") who were already
    // present earlier today.
    //
    // This popup is gated on the lesson being today — backfilling past days
    // shouldn't prompt "did they leave the school?", because that's a live-
    // attendance question. (The course-absent "stayed with class?" popup
    // does NOT have this gate — see the per-student guard.) Compare in
    // Europe/Berlin so a misconfigured-TZ browser doesn't misclassify
    // lessons around midnight.
    const lessonStart = (selectedLesson as any)?.start_datetime as
      | string
      | null
      | undefined;
    const berlinDate = (d: Date) =>
      new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Berlin",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(d);
    const todayBerlin = berlinDate(new Date());
    const lessonDateBerlin =
      typeof lessonStart === "string"
        ? lessonStart.slice(0, 10)
        : lessonStart
          ? berlinDate(new Date(lessonStart))
          : null;
    const isLessonToday =
      !!lessonDateBerlin && lessonDateBerlin === todayBerlin;

    const candidates: Array<{ id: string; name: string }> = [];
    if (isLessonToday && earlierPresentIds && earlierPresentIds.size > 0) {
      const studentsById = new Map<string, any>(
        ((selectedLesson?.students || []) as any[]).map((s: any) => [s.id, s]),
      );
      for (const sid of Object.keys(tempAttendance || {})) {
        if (!earlierPresentIds.has(sid)) continue;
        const entry = tempAttendance[sid];
        if (!entry) continue;
        if (entry.status !== "excused") continue;
        // If the teacher already answered the popup once for this student,
        // don't ask again on a second save click.
        if (entry.leftSchool || entry.elsewhereLocation) continue;
        const stu = studentsById.get(sid);
        if (!stu) continue;
        candidates.push({ id: sid, name: stu.name });
      }
    }

    if (candidates.length > 0) {
      setEarlierGuardStudents(candidates);
      setEarlierGuardOpen(true);
      return;
    }

    await performSave();
  };

  const handleEarlierGuardConfirm = async (
    decisions: EarlierPresentDecision[],
  ) => {
    // Decision → TempAttendanceEntry override mapping:
    //
    //   left_school              → leftSchool=true, excuseReason=reason
    //                              (reason empty when an absence note already
    //                              explains the early leave). useAttendanceSave
    //                              flips DB status to `left_early` because of
    //                              leftSchool, daily-log recompute writes
    //                              check_out_time.
    //   elsewhere_informiert     → status stays excused (Informiert),
    //                              elsewhereLocation=reason. The save mapper
    //                              builds an "Anderswo: <reason>" lesson note.
    //                              Doesn't count as Fehlzeit on Zeugnis.
    //   elsewhere_unclear        → status flips to unexcused → mapStatus()
    //                              writes DB `absent_unclear` (Fehlzeit).
    //                              excuseReason=reason (optional) lands on
    //                              attendance_logs.notes.
    const overrides: Record<
      string,
      {
        leftSchool?: boolean;
        elsewhereLocation?: string;
        excuseReason?: string;
        status?: "present" | "late" | "excused" | "unexcused";
      }
    > = {};
    for (const d of decisions) {
      const reason = (d.reason || "").trim();
      if (d.choice === "left_school") {
        overrides[d.studentId] = {
          leftSchool: true,
          elsewhereLocation: undefined,
          excuseReason: reason || undefined,
        };
      } else if (d.choice === "elsewhere_informiert") {
        overrides[d.studentId] = {
          leftSchool: false,
          elsewhereLocation: reason,
          // The save mapper reads `status` to pick `absent_excused` here.
          status: "excused",
        };
      } else {
        // elsewhere_unclear
        overrides[d.studentId] = {
          leftSchool: false,
          elsewhereLocation: undefined,
          excuseReason: reason || undefined,
          status: "unexcused",
        };
      }
    }
    // Mirror to React state so a follow-up Save click also remembers the
    // teacher's answers (otherwise the popup would re-open).
    setTempAttendance((prev) => {
      const next = { ...prev };
      for (const sid of Object.keys(overrides)) {
        const cur = next[sid];
        if (!cur) continue;
        next[sid] = { ...cur, ...overrides[sid] };
      }
      return next;
    });
    setEarlierGuardOpen(false);
    setEarlierGuardStudents([]);
    // Pass the overrides directly so save isn't blocked on a React commit.
    await performSave(overrides);

    // For every "Auschecken" decision, also write check_out_time on today's
    // student_daily_log. The bulk attendance RPC no longer carries
    // `left_early` on the per-lesson axis (it's day-level only now), so we
    // need a separate call to actually check the kid out. The teacher's
    // Begründung — or, if none, the existing absence note explanation —
    // lands on daily_log.notes via the RPC's "checkout:<staff>:<text>" tag.
    const leftSchoolDecisions = decisions.filter(
      (d) => d.choice === "left_school",
    );
    if (leftSchoolDecisions.length > 0) {
      await Promise.all(
        leftSchoolDecisions.map(async (d) => {
          try {
            await supabase.rpc("ops_check_out_student_react" as any, {
              p_student_id: d.studentId,
              // Initiated from the lesson-edit flow — distinct from the
              // check-in/out kiosk methods ("Allein", "Abholung", …) so the
              // audit trail makes it obvious where the checkout came from.
              p_method: "Klassenbucheintrag",
              p_note: d.reason && d.reason.trim() ? d.reason.trim() : null,
              // Paired-kiosk callers pass the teacher's id from the
              // chip-login session; authenticated callers omit it and
              // the function falls back to auth.uid(). See
              // 20260520_kiosk_ops_check_out_student.sql.
              p_user_id: userIdOverride ?? null,
            } as any);
          } catch (err) {
            // Don't block the dialog: the lesson-level Informiert write
            // already succeeded above. Surface to console so we can chase
            // checkout failures separately.
            // eslint-disable-next-line no-console
            console.error(
              "[EarlierPresentSaveDialog] checkout RPC failed for",
              d.studentId,
              err,
            );
          }
        }),
      );
    }
  };

  const handleEarlierGuardCancel = () => {
    setEarlierGuardOpen(false);
    setEarlierGuardStudents([]);
  };

  const [courseAbsentGuard, setCourseAbsentGuard] = useState<{
    studentId: string;
    status: "excused" | "unexcused";
  } | null>(null);
  const [courseAbsentComment, setCourseAbsentComment] = useState("");
  const courseAbsentStudentName = useMemo(() => {
    if (!courseAbsentGuard) return "das Kind";
    const student = (selectedLesson?.students || []).find(
      (s: any) => s.id === courseAbsentGuard.studentId,
    );
    return student?.name ? formatStudentName(String(student.name)) : "das Kind";
  }, [courseAbsentGuard, selectedLesson?.students, formatStudentName]);

  const setStudentStatusForEdit = useCallback(
    (
      studentId: string,
      status: "present" | "late" | "excused" | "unexcused",
    ) => {
      const sl = selectedLesson as any;
      // Course + parallel main + clicking Inf (Informiert) — the popup is
      // ONLY for the "the kid was here earlier, why aren't they in my course
      // right now?" case. If the student was NOT earlier-present today
      // (U'd all day, never checked in, etc.) the course teacher should be
      // able to mark Inf directly — e.g. a parent calls to say the kid is
      // sick and the course teacher records Informiert immediately. No
      // popup, no transfer; just the Inf on the course.
      if (
        sl?.is_course &&
        sl?.parallel_main_lesson_id &&
        status === "excused" &&
        earlierPresentIds?.has(studentId)
      ) {
        const cur = tempAttendance[studentId]?.status;
        // Also skip the popup when the teacher has *just* marked the
        // student U in this lesson — `earlierPresentIds` says they were
        // here earlier per the daily log, but the U the teacher already
        // entered means "not present right now". Going U → Inf is a
        // refinement of the not-present verdict (e.g. parents called,
        // kid is sick) — no point asking about staying with the class.
        if (cur !== "excused" && cur !== "unexcused") {
          setCourseAbsentComment("");
          setCourseAbsentGuard({ studentId, status });
          return;
        }
      }
      setStudentStatus(studentId, status);
    },
    [selectedLesson, tempAttendance, earlierPresentIds, setStudentStatus],
  );

  const onCourseAbsentNeinUnsicher = () => {
    if (!courseAbsentGuard) return;
    const reason = courseAbsentComment.trim();
    if (!reason) return;
    const { studentId, status } = courseAbsentGuard;
    setCourseAbsentComment("");
    setCourseAbsentGuard(null);
    setStudentStatus(studentId, status);
    setTempAttendance((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        excuseReason: reason,
      },
    }));
  };

  const onCourseAbsentJa = async () => {
    if (!courseAbsentGuard || isTransferring) return;
    const reason = courseAbsentComment.trim();
    if (!reason) return;
    const { studentId } = courseAbsentGuard;
    const ok = await handleStayedWithClassTransfer(studentId, reason);
    if (ok) {
      setCourseAbsentComment("");
      setCourseAbsentGuard(null);
    }
  };

  // ── Bulk "Alle Inf" guard for course lessons with a parallel main ──────
  const [courseAbsentBulkPending, setCourseAbsentBulkPending] = useState<
    string[] | null
  >(null);
  // ── "Alle Spät" confirmation — easy to mis-tap, unusual outcome ────────
  const [allLateConfirmPending, setAllLateConfirmPending] = useState(false);

  const setAllStudentsStatusGuarded = useCallback(
    (status: "present" | "late" | "excused" | "unexcused") => {
      if (status === "late") {
        setAllLateConfirmPending(true);
        return;
      }
      const sl = selectedLesson as any;
      if (
        sl?.is_course &&
        sl?.parallel_main_lesson_id &&
        status === "excused"
      ) {
        const eligible = ((selectedLesson?.students || []) as any[])
          .filter((s) => {
            const prot = (protectionByStudent as any)?.[s.id];
            if (prot?.protected) return false;
            const cur = tempAttendance[s.id]?.status;
            // Already excused/U in this lesson → not eligible (transition
            // is a refinement of the already-decided "not present"
            // verdict, no popup needed). Mirrors the per-student guard.
            if (cur === "excused" || cur === "unexcused") return false;
            // Otherwise the popup is only for the "kid was here earlier"
            // case. A student who was never present today gets Inf'd
            // directly with no popup.
            return earlierPresentIds?.has(s.id) ?? false;
          })
          .map((s) => s.id);
        if (eligible.length > 0) {
          setCourseAbsentComment("");
          setCourseAbsentBulkPending(eligible);
          return;
        }
      }
      setAllStudentsStatus(status);
    },
    [
      selectedLesson,
      tempAttendance,
      protectionByStudent,
      earlierPresentIds,
      setAllStudentsStatus,
    ],
  );

  const onCourseAbsentBulkNeinUnsicher = () => {
    if (!courseAbsentBulkPending) return;
    const reason = courseAbsentComment.trim();
    if (!reason) return;
    const ids = courseAbsentBulkPending;
    setCourseAbsentComment("");
    setCourseAbsentBulkPending(null);
    ids.forEach((sid) => {
      setStudentStatus(sid, "excused");
      setTempAttendance((prev) => ({
        ...prev,
        [sid]: {
          ...(prev[sid] || { status: "excused" }),
          excuseReason: reason,
        },
      }));
    });
  };

  const onCourseAbsentBulkJa = async () => {
    if (!courseAbsentBulkPending || isTransferring) return;
    const reason = courseAbsentComment.trim();
    if (!reason) return;
    const ids = courseAbsentBulkPending;
    for (const sid of ids) {
      const ok = await handleStayedWithClassTransfer(sid, reason);
      if (!ok) return;
    }
    setCourseAbsentComment("");
    setCourseAbsentBulkPending(null);
  };
  const [removeCourseGuestEnrollment, { isLoading: isRemovingGuest }] =
    useRemoveCourseGuestEnrollmentMutation();

  const [guestRemoveTarget, setGuestRemoveTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [guestRemoveError, setGuestRemoveError] = useState<string | null>(null);

  const [setCourseShortNoticeCancelled, { isLoading: isSavingCourseCancel }] =
    useSetCourseShortNoticeCancelledMutation();
  const [courseCancelError, setCourseCancelError] = useState<string | null>(null);

  // Carry-forward / prefill confirmation gate. When the loader has prefilled
  // tempAttendance from previous-lesson status (or daily-log prefills) AND
  // the teacher hasn't saved this lesson yet, show a Bestätigen / Anpassen
  // summary card instead of the full edit grid — matching the inline
  // attendance widget pattern from the Klassenbuch diary view. The full
  // grid is still one tap away ("Anpassen"); the common case (everyone
  // carried over correctly) becomes a single confirm.
  const [adjustOpen, setAdjustOpen] = useState(!!skipPrefillSummary);
  const isPrefilled =
    !recordedMeta && (prefilledFromPrevious || prefilledAny);
  const showPrefillSummary =
    isPrefilled &&
    !adjustOpen &&
    // Course lessons skip the summary card entirely — the carry-forward
    // model on a course is messier (Tagesgäste, parallel main class,
    // course-absent prompts) and a Bestätigen-everyone shortcut doesn't
    // really fit. Open straight into the Anpassen grid instead.
    !((selectedLesson as any)?.is_course) &&
    !((selectedLesson as any)?.kb_locked) &&
    ((selectedLesson?.students || []) as any[]).length > 0;

  const prefillSummary = useMemo(() => {
    const informiert: Array<{ id: string; name: string }> = [];
    const ungeklaert: Array<{ id: string; name: string }> = [];
    const pending: Array<{ id: string; name: string }> = [];
    let anwesend = 0;
    for (const s of (selectedLesson?.students || []) as Array<{
      id: string;
      name: string;
    }>) {
      const entry = tempAttendance[s.id];
      if (!entry) {
        pending.push({ id: s.id, name: s.name });
        continue;
      }
      if (entry.status === "excused") {
        informiert.push({ id: s.id, name: s.name });
      } else if (entry.status === "unexcused") {
        ungeklaert.push({ id: s.id, name: s.name });
      } else {
        anwesend += 1;
      }
    }
    return { informiert, ungeklaert, anwesend, pending };
  }, [tempAttendance, selectedLesson?.students]);

  // Snapshot the initial "noch nicht erfasst" set the first time the
  // summary card mounts on this lesson. We keep those rows rendered as
  // the teacher marks them, instead of letting them blink out the moment
  // they get a status — sudden disappearance was confusing.
  // The "N noch nicht erfasst:" count and the Bestätigen gate still use
  // the LIVE pending list, so progress feedback is honest.
  const [pendingSnapshotIds, setPendingSnapshotIds] = useState<Set<
    string
  > | null>(null);
  // If the user clicks Bestätigen while still pending, we surface a
  // warning instead of silently swallowing the click (previous version
  // disabled the button, which left teachers confused about why nothing
  // happened).
  const [bestaetigenWarning, setBestaetigenWarning] = useState(false);

  // Reset the gate when the lesson identity changes — otherwise switching
  // between tiles keeps the previous lesson's "Anpassen" choice.
  useEffect(() => {
    // On a new lesson, keep `adjustOpen` honest: callers that requested
    // skipPrefillSummary expect every lesson opened through them to skip
    // the card; callers that didn't expect the card to show first.
    setAdjustOpen(!!skipPrefillSummary);
    setPendingSnapshotIds(null);
    setBestaetigenWarning(false);
  }, [selectedLesson?.id, skipPrefillSummary]);

  useEffect(() => {
    if (pendingSnapshotIds !== null) return;
    if (!isPrefilled) return;
    if (prefillSummary.pending.length === 0) return;
    setPendingSnapshotIds(
      new Set(prefillSummary.pending.map((p) => p.id)),
    );
    // Too many pending to comfortably review inside the summary card —
    // jump straight into the full edit grid (Anpassen). The teacher can
    // still tap the "← Zurück zur Übersicht" link to come back.
    if (prefillSummary.pending.length > 5) {
      setAdjustOpen(true);
    }
  }, [pendingSnapshotIds, isPrefilled, prefillSummary.pending]);

  // Clear the warning automatically the moment everyone is marked.
  useEffect(() => {
    if (prefillSummary.pending.length === 0 && bestaetigenWarning) {
      setBestaetigenWarning(false);
    }
  }, [prefillSummary.pending.length, bestaetigenWarning]);

  const enrolledIds = useMemo<Set<string>>(
    () =>
      new Set<string>(
        ((selectedLesson?.students || []) as any[]).map((s: any) =>
          String(s.id),
        ),
      ),
    [selectedLesson?.students],
  );

  const isCourseShortNoticeCancelled = !!(selectedLesson as any)
    ?.course_short_notice_cancelled;
  const hasActionableStudents = ((selectedLesson?.students || []) as any[]).length > 0;
  const isCourseNoActionable = isCourse && !hasActionableStudents;

  // Week-level Klassenbuch approval lock — server-side triggers reject any
  // attendance or diary write once the week is approved. Mirror that here so
  // teachers see a banner + lock icons instead of getting a 500 on save.
  const isKbLocked = !!(selectedLesson as any)?.kb_locked;
  const kbApprovedByName = (selectedLesson as any)?.kb_approved_by_name as
    | string
    | null
    | undefined;
  const kbApprovedAt = (selectedLesson as any)?.kb_approved_at as
    | string
    | null
    | undefined;
  const kbLockTooltip = isKbLocked
    ? `Geprüft${kbApprovedByName ? ` von ${kbApprovedByName}` : ""}${
        kbApprovedAt
          ? ` am ${new Intl.DateTimeFormat("de-DE", {
              timeZone: "Europe/Berlin",
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }).format(new Date(kbApprovedAt))}`
          : ""
      }`
    : "";

  const handleConfirmRemoveGuest = async () => {
    if (!guestRemoveTarget || !lessonId) return;
    setGuestRemoveError(null);
    try {
      await removeCourseGuestEnrollment({
        lessonId,
        studentId: guestRemoveTarget.id,
      }).unwrap();
      setGuestRemoveTarget(null);
      await reloadBundle();
    } catch (err: any) {
      setGuestRemoveError(
        err?.error ||
          err?.message ||
          "Tagesgast konnte nicht entfernt werden.",
      );
      setGuestRemoveTarget(null);
    }
  };

  const handleSetCourseCancelled = async () => {
    if (!lessonId) return;
    setCourseCancelError(null);
    try {
      await setCourseShortNoticeCancelled({
        lessonId,
        isCancelled: true,
        reason: lessonNote,
      }).unwrap();
      await reloadBundle();
    } catch (err: any) {
      setCourseCancelError(
        err?.error || err?.message || "Kurzfristiger Ausfall konnte nicht gespeichert werden.",
      );
    }
  };

  const handleUnsetCourseCancelled = async () => {
    if (!lessonId) return;
    setCourseCancelError(null);
    try {
      await setCourseShortNoticeCancelled({
        lessonId,
        isCancelled: false,
        reason: null,
      }).unwrap();
      await reloadBundle();
    } catch (err: any) {
      setCourseCancelError(
        err?.error || err?.message || "Ausfall-Status konnte nicht aufgehoben werden.",
      );
    }
  };

  return (
    <div className="space-y-4">
      {isKbLocked && (
        <div
          className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900"
          title={kbLockTooltip}
        >
          <Lock className="size-4 mt-0.5 flex-shrink-0 text-emerald-700" />
          <div>
            <div className="font-medium">
              Diese Woche ist bereits geprüft — keine Änderungen mehr möglich.
            </div>
            <div className="text-xs text-emerald-800 mt-0.5">
              {kbLockTooltip}
            </div>
          </div>
        </div>
      )}
      <EarlierPresentSaveDialog
        open={earlierGuardOpen}
        students={earlierGuardStudents}
        onCancel={handleEarlierGuardCancel}
        onConfirm={handleEarlierGuardConfirm}
        formatStudentName={formatStudentName}
        kbStudentIds={kbStudentIds}
        kbStudentTimes={kbStudentTimes}
        lessonStartDatetime={selectedLesson?.start_datetime ?? null}
        studentIdsWithCoveringAbsenceNote={studentIdsWithCoveringAbsenceNote}
        absenceNoteTextByStudent={earlierGuardNoteText}
      />
      <AlertDialog
        open={!!courseAbsentGuard}
        onOpenChange={(open) => {
          if (!open) {
            setCourseAbsentComment("");
            setCourseAbsentGuard(null);
          }
        }}
      >
        <AlertDialogContent>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 h-8 w-8"
            onClick={() => {
              setCourseAbsentComment("");
              setCourseAbsentGuard(null);
            }}
            aria-label="Dialog schließen"
          >
            <X className="h-4 w-4" />
          </Button>
          <AlertDialogHeader>
            <AlertDialogTitle>Kurzer Kommentar</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Textarea
              id="course-absent-comment"
              placeholder={`z. B. ${courseAbsentStudentName}: bei der Klasse geblieben, weil ... / nicht gefunden / heute abgemeldet`}
              value={courseAbsentComment}
              onChange={(e) => setCourseAbsentComment(e.target.value)}
              rows={2}
              className="min-h-[72px]"
            />
          </div>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isTransferring || !courseAbsentComment.trim()}
              onClick={onCourseAbsentNeinUnsicher}
            >
              Nein / Unsicher
            </Button>
            <Button
              type="button"
              disabled={isTransferring || !courseAbsentComment.trim()}
              onClick={() => void onCourseAbsentJa()}
            >
              Ja
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* "Inf Alle" bulk variant of the course-absent guard */}
      <AlertDialog
        open={!!courseAbsentBulkPending}
        onOpenChange={(open) => {
          if (!open) {
            setCourseAbsentComment("");
            setCourseAbsentBulkPending(null);
          }
        }}
      >
        <AlertDialogContent>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 h-8 w-8"
            onClick={() => {
              setCourseAbsentComment("");
              setCourseAbsentBulkPending(null);
            }}
            aria-label="Dialog schließen"
          >
            <X className="h-4 w-4" />
          </Button>
          <AlertDialogHeader>
            <AlertDialogTitle>Kurzer Kommentar</AlertDialogTitle>
            <AlertDialogDescription>
              {(courseAbsentBulkPending?.length ?? 0)} Schüler*innen werden als
              Informiert markiert. Sind sie bei der Klasse geblieben?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Textarea
              id="course-absent-bulk-comment"
              placeholder="z. B. bei der Klasse geblieben, weil ... / nicht gefunden / heute abgemeldet"
              value={courseAbsentComment}
              onChange={(e) => setCourseAbsentComment(e.target.value)}
              rows={2}
              className="min-h-[72px]"
            />
          </div>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isTransferring || !courseAbsentComment.trim()}
              onClick={onCourseAbsentBulkNeinUnsicher}
            >
              Nein / Unsicher
            </Button>
            <Button
              type="button"
              disabled={isTransferring || !courseAbsentComment.trim()}
              onClick={() => void onCourseAbsentBulkJa()}
            >
              Ja
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* "Alle Spät" confirmation — easy to mis-tap, unusual outcome */}
      <AlertDialog
        open={allLateConfirmPending}
        onOpenChange={(open) => {
          if (!open) setAllLateConfirmPending(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alle als verspätet markieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher? Das ist ungewöhnlich — meist sind nur einzelne
              Schüler*innen verspätet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setAllLateConfirmPending(false)}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={() => {
                setAllLateConfirmPending(false);
                setAllStudentsStatus("late");
              }}
            >
              Ja, alle verspätet
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tagesgast remove confirmation */}
      <AlertDialog
        open={!!guestRemoveTarget}
        onOpenChange={(open) => {
          if (!open && !isRemovingGuest) setGuestRemoveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tagesgast entfernen?</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="text-sm text-gray-700">
            {guestRemoveTarget
              ? `${formatStudentName(guestRemoveTarget.name)} wird aus diesem Kurs als Tagesgast entfernt.`
              : ""}
          </div>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isRemovingGuest}
              onClick={() => setGuestRemoveTarget(null)}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              disabled={isRemovingGuest}
              onClick={() => void handleConfirmRemoveGuest()}
              className="bg-red-600 hover:bg-red-700"
            >
              {isRemovingGuest ? "Entferne…" : "Entfernen"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Prefill alert */}
      {!showPrefillSummary && !recordedMeta && (prefilledFromPrevious || prefilledAny) && (
        <div
          role="alert"
          className="rounded-md border-2 border-amber-500 bg-amber-100 text-amber-900 px-4 py-3 text-sm lg:text-base font-medium"
        >
          {isMobile
            ? "Anwesenheit wurde übernommen. Bitte prüfen und auf „Speichern\" klicken"
            : "Anwesenheit wurde übernommen. Bitte prüfen und auf „Anwesenheit speichern\" klicken"}
        </div>
      )}

      {/* Kept-in-main-lesson banner (course popups only) */}
      {keptInMainLesson.length > 0 && (
        <div className="rounded-md border border-amber-400 bg-amber-50 px-4 py-3">
          <h4 className="text-sm font-semibold text-amber-900 mb-2">
            ⚠️ Im Klassenunterricht geblieben ({keptInMainLesson.length})
          </h4>
          <div className="space-y-1">
            {keptInMainLesson.map((s) => (
              <div key={s.id} className="text-sm text-amber-800">
                <span className="font-medium">{formatStudentName(s.name)}</span>
                {s.mode === "pending" ? (
                  <span className="ml-2 inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900">
                    Ausstehend
                  </span>
                ) : (
                  <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-800">
                    ✓ Anwesend
                  </span>
                )}
                {s.notes && (
                  <span className="text-amber-600 italic ml-1">— {s.notes}</span>
                )}
                {/* Only offer "zurück in Kurs" once the home teacher has actually
                    recorded the student present (mode='present'). For pending
                    transfers the home teacher hasn't acted yet, so reversing
                    here would be a same-side undo, not a correction. */}
                {s.mode === "present" && s.fromLessonId && (
                  <button
                    type="button"
                    disabled={isTransferring}
                    onClick={() => onReturnToCourseConfirm(s.id, s.fromLessonId!)}
                    className="ml-2 text-xs px-2 py-0.5 rounded bg-amber-200 text-amber-900 hover:bg-amber-300 disabled:opacity-50"
                    title="Korrektur: zurück in Kurs buchen"
                  >
                    zurück in Kurs
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bänder log entry form — only mounted when the bundle says this
          lesson's class has a band assignment for this day+period. Skipping
          the mount avoids the form's 4 RPC queries (bands, assignments,
          periods, log entries) for the common case where no band exists. */}
      {selectedLesson?.class_id &&
        selectedLesson?.start_datetime &&
        (selectedLesson as any)?.has_band_assignment && (
          <BandLogEntryForm
            ref={bandFormRef}
            lessonId={selectedLesson.id}
            classId={selectedLesson.class_id}
            dateStr={selectedLesson.start_datetime}
            // The attendance bundle's lesson row exposes `period_number`; the
            // planner-side payload uses `block_number`. Accept either so the
            // band form renders in both contexts without a fallback round-trip
            // through the periods lookup.
            periodNumber={
              (selectedLesson as any).period_number ??
              (selectedLesson as any).block_number ??
              null
            }
            // Pre-fetched from get_attendance_bundle_v2 — skips the form's
            // 4 separate RPCs entirely.
            bundledBandData={(selectedLesson as any)?.band_data}
          />
        )}

      {/* Diary input (hidden for no-actionable course KA flow) */}
      {!isCourseNoActionable && (
        <>
          <Label
            htmlFor="lesson-note"
            className="font-medium text-center lg:text-left block text-lg"
          >
            Klassenbuch-Eintrag
          </Label>
          <Textarea
            id="lesson-note"
            placeholder="Notizen zur Stunde, behandelte Themen, besondere Vorkommnisse..."
            value={lessonNote}
            onChange={(e) => setLessonNote(e.target.value)}
            className="min-h-[80px]"
            readOnly={isKbLocked}
            disabled={isKbLocked}
          />
          {diaryMeta && lessonNote.trim() && (
            <div className="text-xs text-gray-500 mt-1">
              {(() => {
                const d = new Date(diaryMeta.atISO);
                const date = new Intl.DateTimeFormat("de-DE", {
                  timeZone: "Europe/Berlin",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }).format(d);
                const time = new Intl.DateTimeFormat("de-DE", {
                  timeZone: "Europe/Berlin",
                  hour: "2-digit",
                  minute: "2-digit",
                }).format(d);
                return `Bisher geschrieben von ${diaryMeta.name} am ${date} um ${time} Uhr`;
              })()}
            </div>
          )}

          {/* Hausaufgaben — collapsed behind a button by default. Expands
              automatically when the lesson already has homework saved, so
              editing existing homework doesn't need an extra click. */}
          {setLessonHomework ? (
            <HomeworkField
              value={lessonHomework}
              onChange={setLessonHomework}
              hadInitialHomework={initialLessonHomework.trim().length > 0}
              disabled={isKbLocked}
            />
          ) : null}
        </>
      )}

      {/* Prefill summary card — shown when attendance was carried forward
          and not yet confirmed/adjusted. Mirrors the inline-attendance
          widget pattern from the diary view: list absent students grouped
          by Informiert / Ungeklärt, present count, then Bestätigen (save
          as-is) or Anpassen (reveal the full edit grid). */}
      {showPrefillSummary && (() => {
        // Compact name for the lists: first name + first two letters of
        // last name (e.g. "Polat Petrov Ognyanov" → "Polat Og"). Keeps
        // the card readable on mobile where 21 names would otherwise
        // wrap into a wall of text.
        const shortName = (full: string) => {
          const trimmed = (full || "").trim();
          if (!trimmed) return "";
          const parts = trimmed.split(/\s+/);
          if (parts.length < 2) return trimmed;
          const first = parts[0];
          const last = parts[parts.length - 1];
          return `${first} ${last.slice(0, 2)}`;
        };
        return (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-3">
          {bestaetigenWarning && prefillSummary.pending.length > 0 && (
            <div
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 text-red-700 px-3 py-2 text-sm animate-in fade-in slide-in-from-top-1 duration-200"
            >
              Bestätigen erst möglich, wenn alle Schüler*innen erfasst wurden.
            </div>
          )}
          <div className="text-sm space-y-1">
            {pendingSnapshotIds && pendingSnapshotIds.size > 0 && (
              // Carry-forward couldn't decide for these students — render
              // them as live edit rows inline so the teacher can mark
              // them straight from the summary card (no Anpassen detour).
              //
              // The row list is sticky against the snapshot taken when the
              // card first opened — once a teacher marks one of them, the
              // row STAYS visible (now showing the new status) instead of
              // vanishing mid-tap. The header label and the Bestätigen
              // gate still react to the live `pending` count.
              //
              // Layout matches the regular edit grid: a MarkAllHeader on
              // top (column labels + Alle buttons) above one EditStudentRow
              // per pending kid. Alle is hidden when there's only one
              // pending (single-row bulk action is meaningless).
              <div className="space-y-1">
                <div className="space-y-1">
                  {(() => {
                    const liveCount = prefillSummary.pending.length;
                    const labelEl =
                      liveCount > 0 ? (
                        <span className="text-sm font-semibold text-orange-600">
                          {liveCount} noch nicht erfasst:
                        </span>
                      ) : (
                        <span className="text-sm font-semibold text-green-600">
                          Alle markiert ✓
                        </span>
                      );
                    // Use the snapshot size (not live count) to decide
                    // whether to render the MarkAllHeader — once shown,
                    // it shouldn't flicker out as the teacher resolves
                    // students one by one.
                    return pendingSnapshotIds && pendingSnapshotIds.size > 1 ? (
                      <MarkAllHeader
                        isMobile={isMobile}
                        setAllStudentsStatus={setAllStudentsStatusGuarded}
                        leftLabel={labelEl}
                      />
                    ) : (
                      <div className="pb-1">{labelEl}</div>
                    );
                  })()}
                  {((selectedLesson?.students || []) as Array<{
                    id: string;
                    name: string;
                    className?: string;
                    isGuest?: boolean;
                  }>)
                    // Use the frozen snapshot — students that were
                    // pending when the summary card first opened. They
                    // stay rendered as the teacher marks them so the row
                    // doesn't vanish out from under their tap.
                    .filter((s) =>
                      pendingSnapshotIds
                        ? pendingSnapshotIds.has(s.id)
                        : prefillSummary.pending.some((p) => p.id === s.id),
                    )
                    .map((student) => (
                      <EditStudentRow
                        key={`prefill-pending-${student.id}`}
                        student={student}
                        attendance={tempAttendance[student.id]}
                        isMobile={isMobile}
                        isKb={kbStudentIds.has(student.id)}
                        isCourse={isCourse}
                        isPending={true}
                        pendingRed={true}
                        selectedLesson={selectedLesson}
                        setStudentStatus={setStudentStatusForEdit}
                        setTempAttendance={setTempAttendance}
                        formatStudentName={formatStudentName}
                        hasCoveringAbsenceNote={lessonCoveredStudentIds.has(
                          student.id,
                        )}
                      />
                    ))}
                </div>
              </div>
            )}
            {/* Title for the carry-forward block below — only shown when
                there's actually carry-forward content (any of ungeklärt,
                Informiert, or a present count), to avoid a stranded
                heading on lessons with nothing carried over. */}
            {(prefillSummary.ungeklaert.length > 0 ||
              prefillSummary.informiert.length > 0 ||
              prefillSummary.anwesend > 0) && (
              <div className="pt-3 mt-2 border-t border-gray-200 text-sm font-semibold text-gray-800">
                Aus bisheriger Stunde übernehmen oder anpassen
              </div>
            )}
            {prefillSummary.ungeklaert.length > 0 && (
              <div className="text-red-600">
                <span className="font-semibold">
                  {prefillSummary.ungeklaert.length} ungeklärt:
                </span>{" "}
                {prefillSummary.ungeklaert.map((s) => shortName(s.name)).join(", ")}
              </div>
            )}
            {prefillSummary.informiert.length > 0 && (
              <div className="text-blue-600">
                <span className="font-semibold">
                  {prefillSummary.informiert.length} Informiert:
                </span>{" "}
                {prefillSummary.informiert.map((s) => shortName(s.name)).join(", ")}
              </div>
            )}
            <div className="text-gray-900">
              <span className="font-semibold">
                {prefillSummary.anwesend} Anwesend
              </span>
            </div>
          </div>
          <div className="space-y-2 pt-1">
            <div className="flex flex-row items-center gap-2">
              <Button
                type="button"
                onClick={async () => {
                  if (prefillSummary.pending.length > 0) {
                    // Still pending — surface a warning instead of silently
                    // doing nothing. Disabling the button confused teachers
                    // ("nothing happens when I click").
                    setBestaetigenWarning(true);
                    return;
                  }
                  await saveAttendance();
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Bestätigen
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAdjustOpen(true)}
              >
                Anpassen
              </Button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Guests-elsewhere: students whose normal slot is here but who are
          attending another course as Tagesgast today. They are not in the
          active roster — this panel just tells the original teacher where
          they went and why. */}
      {Array.isArray((selectedLesson as any)?.guests_elsewhere) &&
        (selectedLesson as any).guests_elsewhere.length > 0 && (
          <ul className="rounded-md border border-blue-300 bg-blue-50 p-3 space-y-0.5 text-sm text-blue-900">
            {((selectedLesson as any).guests_elsewhere as Array<{
              student_id: string;
              name: string;
              other_course_name: string;
              guest_reason: string | null;
            }>).map((g) => (
              <li key={g.student_id}>
                <span className="font-medium">{formatStudentName(g.name)}</span>
                {" — Gast "}
                {g.other_course_name ? `im ${g.other_course_name} ` : ""}
                {"heute wegen "}
                <span className="italic">
                  {g.guest_reason && g.guest_reason.trim()
                    ? g.guest_reason
                    : "keine Begründung angegeben"}
                </span>
              </li>
            ))}
          </ul>
        )}

      {/* Concurrent course overlap boxes */}
      <ConcurrentCoursePanel
        courseOverlaps={courseOverlaps}
        concurrentCourseAttendance={concurrentCourseAttendance}
        transferredStudentIds={transferredStudentIds}
        lessonId={lessonId}
        isTransferring={isTransferring}
        pendingTransfer={pendingTransfer}
        setPendingTransfer={setPendingTransfer}
        transferReason={transferReason}
        setTransferReason={setTransferReason}
        onTransferConfirm={onTransferConfirm}
        variant="edit"
      />

      {/* Doppelstunde info panels */}
      <DoppelstundeInfoPanel
        nextLessonIsCancelled={nextLessonIsCancelled}
        nextLessonSubject={nextLessonSubject}
        nextLessonCourses={nextLessonCourses}
      />

      {/* Desktop hint */}
      {!isMobile && !isCourseNoActionable && (
        <div className="text-sm text-gray-600 text-center lg:text-left">
          Klicken Sie auf die Symbole, um die Anwesenheit zu markieren
        </div>
      )}

      {/* "Zurück" link — only when the teacher came in via the prefill
          summary card and pressed Anpassen. Lets them undo the choice
          and return to the Bestätigen / Anpassen view. */}
      {!showPrefillSummary && adjustOpen && isPrefilled && (
        <div className="flex justify-start">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs text-gray-600 px-2"
            onClick={() => setAdjustOpen(false)}
          >
            ← Zurück zur Übersicht
          </Button>
        </div>
      )}

      {/* Tagesgast picker (course lessons only) + save button — always
          on one row, anchored to the right (mobile + desktop). On desktop
          the Tagesgast card additionally grows to fill the row width.
          Hidden behind the prefill-summary card (Bestätigen / Anpassen)
          until the teacher explicitly chooses Anpassen. */}
      {!showPrefillSummary && (
      <div className="flex flex-row justify-end items-start gap-2">
        {isCourse && (
          <div className="lg:flex-1">
            <TagesgastCard
              lessonId={lessonId}
              lessonName={(selectedLesson as any)?.subject}
              enrolledIds={enrolledIds}
              reloadBundle={reloadBundle}
              userIdOverride={userIdOverride}
            />
          </div>
        )}
        {!isCourseNoActionable && (
          <div className="flex justify-center lg:justify-end">
            <Button
              onClick={handleSaveAll}
              disabled={isKbLocked}
              title={isKbLocked ? kbLockTooltip : undefined}
              className="bg-green-600 hover:bg-green-700"
            >
              {isKbLocked && <Lock className="size-4 mr-1.5" />}
              {isMobile ? "Speichern" : "Anwesenheit speichern"}
            </Button>
          </div>
        )}
      </div>
      )}
      {guestRemoveError && (
        <div className="text-xs text-red-600">{guestRemoveError}</div>
      )}

      {isCourseNoActionable && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold bg-amber-200 text-amber-900">
              KA
            </span>
            <div className="text-sm font-medium text-amber-900">
              Kurzfristig ausgefallen
            </div>
          </div>
          <div className="text-xs text-amber-800">
            Bitte Grund angeben, warum alle Kinder heute nicht im Kurs waren.
          </div>
          <Textarea
            id="lesson-note-ka"
            placeholder="Grund für kurzfristigen Ausfall eingeben..."
            value={lessonNote}
            onChange={(e) => setLessonNote(e.target.value)}
            className="min-h-[90px] bg-white"
          />
          <div className="flex gap-2 justify-end">
            {isCourseShortNoticeCancelled && (
              <Button
                type="button"
                variant="outline"
                onClick={handleUnsetCourseCancelled}
                disabled={isSavingCourseCancel}
              >
                Ausfall aufheben
              </Button>
            )}
            <Button
              type="button"
              onClick={handleSetCourseCancelled}
              disabled={
                isSavingCourseCancel ||
                !String(lessonNote || "").trim()
              }
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSavingCourseCancel
                ? "Speichere..."
                : isCourseShortNoticeCancelled
                  ? "Ausfall aktualisieren"
                  : "Kurzfristig ausgefallen"}
            </Button>
          </div>
          {courseCancelError && (
            <div className="text-xs text-red-600">{courseCancelError}</div>
          )}
        </div>
      )}

      {/* KB auto-excused warning */}
      {kbStudentIds.size > 0 &&
        selectedLesson?.students &&
        (() => {
          const students = (selectedLesson.students as any[]) || [];
          const names = Array.from(kbStudentIds)
            .map((id) => {
              const s = students.find((st: any) => st.id === id);
              const t = kbStudentTimes.get(id);
              const firstName = s ? s.name.split(" ")[0] : id.slice(0, 6);
              return t ? `${firstName} (${t.start}–${t.end})` : firstName;
            })
            .join(", ");
          return (
            <div className="rounded-md border border-amber-400 bg-amber-50 text-amber-800 px-3 py-2 text-xs font-medium">
              ⚠️ Kurzbeschulung (KB): {names} — außerhalb dieser Zeiten autom.
              entschuldigt. Falls doch anwesend, bitte als anwesend markieren.
            </div>
          );
        })()}

      {/* Distributed-guest warning: kids zugeteilt from a cancelled lesson elsewhere */}
      {selectedLesson?.students &&
        (() => {
          const students = (selectedLesson.students as any[]) || [];
          const guests = students.filter((s) => s?.isGuest);
          if (guests.length === 0) return null;
          const bySource = new Map<string, string[]>();
          for (const g of guests) {
            const key = g.guestSourceClassName
              ? `${g.guestSourceClassName}${g.guestSourceLabel ? ` (${g.guestSourceLabel})` : ""}`
              : "Anderer Klasse";
            const list = bySource.get(key) || [];
            list.push(String(g.name || "").trim() || "—");
            bySource.set(key, list);
          }
          return (
            <div className="rounded-md border border-amber-400 bg-amber-50 text-amber-800 px-3 py-2 text-xs font-medium space-y-1">
              <div>
                ⚠️ {guests.length} Schüler:in
                {guests.length === 1 ? "" : "nen"} aus anderer Klasse zugeteilt
                — bitte Anwesenheit miterfassen.
              </div>
              {Array.from(bySource.entries()).map(([source, names]) => (
                <div key={source} className="font-normal">
                  <span className="font-semibold">{source}:</span>{" "}
                  {names.join(", ")}
                </div>
              ))}
            </div>
          );
        })()}

      {/* Mark-all header + student list — hidden under the prefill-summary
          card until the teacher clicks Anpassen. */}
      {!showPrefillSummary && !(isCourse && !hasActionableStudents) && (
        <div
          className={
            isKbLocked ? "pointer-events-none opacity-60 select-none" : ""
          }
          aria-disabled={isKbLocked || undefined}
        >
          <MarkAllHeader
            isMobile={isMobile}
            setAllStudentsStatus={setAllStudentsStatusGuarded}
          />
          <DebugOverlay id="ATT-010-EDIT" name="AttendanceDialog.Edit">
            <div className="space-y-1">
              {(() => {
            const arr = [...(selectedLesson.students || [])];

            // Freeze sort order: only re-sort when lesson changes, not on every click
            const lessonKey = selectedLesson.id || null;
            if (
              lessonKey !== frozenEditLessonIdRef.current ||
              frozenEditOrderRef.current.length === 0
            ) {
              frozenEditLessonIdRef.current = lessonKey;
              arr.sort((a: any, b: any) => {
                // Sort priority: 0=pending, 1=excused/unexcused, 2=present/late
                const getPriority = (id: string) => {
                  const entry = tempAttendance[id];
                  if (!entry) return 0; // pending first
                  if (
                    entry.status === "excused" ||
                    entry.status === "unexcused"
                  )
                    return 1; // absent next
                  return 2; // present/late last
                };
                const aPri = getPriority(a.id);
                const bPri = getPriority(b.id);
                if (aPri !== bPri) return aPri - bPri;
                return compareNames(String(a.name || ""), String(b.name || ""));
              });
              frozenEditOrderRef.current = arr.map((s: any) => s.id);
            } else {
              // Use frozen order
              const byId = new Map(arr.map((s: any) => [s.id, s]));
              arr.length = 0;
              for (const id of frozenEditOrderRef.current) {
                const s = byId.get(id);
                if (s) arr.push(s);
              }
            }

            // Show pending in red when <5 pending and some actual attendance recorded
            const pendingCount = arr.filter(
              (s: any) => !tempAttendance[s.id],
            ).length;
            const hasActual = arr.some((s: any) => {
              const st = tempAttendance[s.id]?.status;
              return st === "present" || st === "late" || st === "unexcused";
            });
            const pendingRed =
              hasActual && pendingCount > 0 && pendingCount < 5;
            return arr.map((student: any) => ({
              student,
              pendingRed,
            }));
              })().map(({ student, pendingRed }: any) => {
                const attendance = tempAttendance[student.id];
                const isPending = !attendance;
                return (
                  <EditStudentRow
                    key={student.id}
                    student={student}
                    attendance={attendance}
                    isMobile={isMobile}
                    isKb={kbStudentIds.has(student.id)}
                    isCourse={isCourse}
                    isPending={isPending}
                    pendingRed={pendingRed}
                    selectedLesson={selectedLesson}
                    setStudentStatus={setStudentStatusForEdit}
                    setTempAttendance={setTempAttendance}
                    formatStudentName={formatStudentName}
                    hasCoveringAbsenceNote={lessonCoveredStudentIds.has(
                      student.id,
                    )}
                    onRemoveGuest={
                      isCourse && student.isGuest
                        ? () =>
                            setGuestRemoveTarget({
                              id: student.id,
                              name: student.name,
                            })
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </DebugOverlay>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Hausaufgaben sub-section
// ============================================================================

/**
 * Collapsible Hausaufgaben input. Renders as a "+ Hausaufgaben" button when
 * the lesson has no homework yet; expands into a Textarea on click or
 * whenever existing homework is loaded. Empty unmount = nothing changed,
 * which the save hook uses to skip the diary-upsert RPC.
 */
function HomeworkField({
  value,
  onChange,
  hadInitialHomework,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  hadInitialHomework: boolean;
  disabled?: boolean;
}) {
  const [expanded, setExpanded] = useState(hadInitialHomework);

  // If the loader brings in new homework after first render (e.g. a
  // post-save refetch), make sure the field becomes visible.
  useEffect(() => {
    if (hadInitialHomework) setExpanded(true);
  }, [hadInitialHomework]);

  if (!expanded) {
    return (
      <div className="pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => setExpanded(true)}
        >
          + Hausaufgaben
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1 pt-1">
      <Label
        htmlFor="lesson-homework"
        className="font-medium text-center lg:text-left block text-base"
      >
        Hausaufgaben
      </Label>
      <Textarea
        id="lesson-homework"
        placeholder="Hausaufgaben für die nächste Stunde…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[60px]"
        readOnly={disabled}
        disabled={disabled}
      />
    </div>
  );
}
